import is from "@sindresorhus/is";
import traverseSchema from "json-schema-traverse";
import lodashGet from "lodash/get";
import { sanitizePathSegment, splitPathBySymbolUnlessWithinQuotesOrBackticks } from "../path";
import { MappingsJSONSchema, MappingsJSONSchemaDefinition } from "./jsonSchema";

export const getSchemaAutocompleteEntries = (
  schema: MappingsJSONSchema | MappingsJSONSchemaDefinition,
  dataPath: string = "",
): Record<string, string> => {
  const parsedDataPath = dataPath.trim();
  if (parsedDataPath === "") {
    const autocompleteEntries: Record<string, string> = {};
    /**
     * `pointersMap` is used to track the relationship between the parent and the child relationship in the context of propertyPath.
     * The key is the `jsonPtr`, the value is the path to a property in an object described by the schema.
     * @example
     * {
     *   '/items': [0],
     *   '/items/properties/foo': [0].foo
     * }
     */
    const pointersMap: Record<string, string> = {};

    traverseSchema(schema, {
      /**
       * We do not need to walk through all the keys at the moment.
       */
      allKeys: false,
      cb: (currentSchema, jsonPtr, _rootSchema, parentJsonPtr, parentKeyword, parentSchema, currentPropertyKey) => {
        const currentPropertySchema = currentSchema as MappingsJSONSchema | MappingsJSONSchemaDefinition;
        const debugData = {
          currentSchema,
          jsonPtr,
          parentJsonPtr,
          parentKeyword,
          parentSchema,
          keyIndex: currentPropertyKey,
        };

        const isRootSchemaDefinition = parentJsonPtr === undefined;
        if (isRootSchemaDefinition) {
          return;
        }

        const isEnum = is.number(currentPropertyKey);
        if (isEnum) {
          return;
        }

        const isArrayPropertyDefinition = parentKeyword === "items";
        if (isArrayPropertyDefinition) {
          const parentPath = pointersMap[parentJsonPtr];
          if (!parentPath) {
            return;
          }

          const propertyPath = `${parentPath}[0]`;
          autocompleteEntries[propertyPath] = currentPropertySchema.type as string;
          pointersMap[jsonPtr] = propertyPath;

          return;
        }

        if (currentPropertyKey == null) {
          throw new Error(`
            Unsupported. The 'keyIndex' is not defined.
            Debug data: ${JSON.stringify(debugData)}`);
        }

        /**
         * @example
         * Given the schema {"type": "object", "properties": {"foo": {type: "string"}, "bar": {type:"number"}}}
         * Both "foo" and "bar" are considered "firstLevelOfNestingProperty".
         */
        const isFirstLevelOfNestingProperty = jsonPtr === "";
        if (isFirstLevelOfNestingProperty) {
          const sanitizedPropertyPath = sanitizePathSegment(currentPropertyKey);
          autocompleteEntries[sanitizedPropertyPath] = currentPropertySchema.type as string;
          pointersMap[jsonPtr] = sanitizedPropertyPath;

          return;
        }

        if (parentKeyword == null) {
          throw new Error(`
          Unsupported. The 'parentKeyword' is not defined.
          Debug data: ${JSON.stringify(debugData)}`);
        }

        const parentPath = pointersMap[parentJsonPtr];
        if (parentPath) {
          const sanitizedPropertyPath = `${parentPath}.${sanitizePathSegment(currentPropertyKey)}`;
          autocompleteEntries[sanitizedPropertyPath] = currentPropertySchema.type as string;
          pointersMap[jsonPtr] = sanitizedPropertyPath;

          return;
        }

        const sanitizedPropertyPath = sanitizePathSegment(currentPropertyKey);
        autocompleteEntries[sanitizedPropertyPath] = currentPropertySchema.type as string;
        pointersMap[jsonPtr] = sanitizedPropertyPath;

        return;
      },
    });

    return autocompleteEntries;
  }

  const schemaToTraverse = getSchemaDefinitionForDataPath(schema, dataPath);
  if (!schemaToTraverse) {
    return {};
  }

  if (schemaToTraverse.type === "object" && schemaToTraverse.properties) {
    const schemaProperties = schemaToTraverse.properties;
    const schemaPropertiesEntries = Object.entries(schemaProperties);

    return schemaPropertiesEntries.reduce(
      (acc, [propertyName, propertyDefinition]) => {
        const sanitizedPropertyName = sanitizePathSegment(propertyName);
        acc[sanitizedPropertyName] = propertyDefinition.type as string;
        return acc;
      },
      {} as Record<string, string>,
    );
  }

  const isArrayType = schemaToTraverse.type === "array";

  /**
   * At this point, schema is neither an object nor an array.
   * Returning an empty object as autocomplete entries in this case.
   */
  if (!isArrayType) {
    return {};
  }

  /**
   * At this point, schema is an array which does not contain items (this shouldn't happen, but according to types it's possible).
   */
  if (schemaToTraverse.items == null) {
    return {};
  }

  /**
   * At this point, schema is an array which contains `items` but their `type` is not defined.
   * Again, shouldn't happen but according to types it's possible. This check is mostly to make TS happy.
   */
  if (!("type" in schemaToTraverse.items)) {
    return {};
  }

  if (schemaToTraverse.items.type == null) {
    return {};
  }

  /**
   * There might be multiple `type`s defined in for schema's `items`.
   * Since we don't have a good way of handling multiple `type`s, we're defaulting to the first one.
   */
  const itemType = Array.isArray(schemaToTraverse.items.type)
    ? schemaToTraverse.items.type[0]
    : schemaToTraverse.items.type;

  return { "[0]": itemType };
};

/**
 * Given a schema and a path to a property of an object that adheres to the schema,
 * returns a schema definition for that given property.
 */
export const getSchemaDefinitionForDataPath = (
  schema: MappingsJSONSchema | MappingsJSONSchemaDefinition,
  dataPath: string,
): MappingsJSONSchemaDefinition | undefined => {
  const pathAsPathParts = pathToPathParts(dataPath);
  const schemaCompatiblePathParts = pathAsPathParts.flatMap((pathPart) => {
    const isArrayItemNumber = is.number(pathPart);
    if (isArrayItemNumber) {
      return ["items"];
    }

    return ["properties", pathPart];
  });

  if (schema.type !== "array") {
    return lodashGet(schema, schemaCompatiblePathParts);
  }

  // When the schema represents a root-level array, and the path already contains the top-level "items" path part
  if (schemaCompatiblePathParts[0] === "items") {
    return lodashGet(schema, schemaCompatiblePathParts);
  }

  // When top-level "items" path part is missing for the root-level array schema we inject it
  // to support autocomplete within root-level array schemas
  const schemaCompatiblePathPartsWithRootArray = ["items", ...schemaCompatiblePathParts];
  return lodashGet(schema, schemaCompatiblePathPartsWithRootArray);
};

/**
 * The `lodash.get(object: Object, path: string)` does not support keys that contain dots.
 * @example
 *  Given the following
 *  const data = {
 *    foo: {"bar.baz": 1}
 *  }
 *
 *  The lodash.get(data, 'foo."bar.baz"') will return undefined.
 *
 * To make sure the `lodash.get` works with keys that contain dots, an alternative `lodash.get` syntax must be used.
 * @example
 *  Given the following
 *  const data = {
 *    foo: {"bar.baz": 1}
 *  }
 *
 *  The lodash.get(data, ["foo", "bar.baz"]) will return 1.
 *
 * The `pathToPathParts` function transforms the to lodash.get compatible paths array.
 * @example
 *  Given the following `path`
 *  'key.`complex.key.dots`.simpleKey'
 *
 *  The path parts will look as following
 *  ["key", "complex.key.dots", "simpleKey"]
 *
 * @example
 *  Given the following `path`
 *  'foo.`bar.baz_wow`[0].`complex_key.dot`'
 *
 *  The path parts will look as following
 *  ["foo", "bar.baz_wow", 0, "complex_key.dot"]
 *
 * @example
 *  Given the following 'path`
 *  'foo.`bar.baz`.[0].`complex_key`'
 *
 *  The path parts will look as following
 *  ["foo", "bar.baz", 0, "complex_key"]
 */
const pathToPathParts = (path: string): (string | number)[] => {
  const splitCharacter = "##__SPLIT__##";

  /**
   * Split by dots but only by dots that are not contained within "" characters
   * @example 'foo.`bar.baz`' => ["foo", "bar.baz"]
   * @example 'foo.`bar.baz`.[0]' => ["foo", "bar.baz", 0]
   * @example 'foo.`bar.baz`[0]' => ["foo", "bar.baz", 0]
   */
  const splitByDotOutsideOfQuotationMarks = splitPathBySymbolUnlessWithinQuotesOrBackticks(path, ".");
  return splitByDotOutsideOfQuotationMarks.flatMap((pathPart) => {
    if (!pathPart.includes("[")) {
      return pathPart.replace(/`/g, "");
    }

    /**
     * @example "[0]" => 0
     */
    if (/^\[\d+\]$/.test(pathPart)) {
      return parseInt(pathPart.replace("[", "").replace("]", ""), 10);
    }

    /**
     * @example '`bar.baz`[0]' => '`bar.baz`##__SPLIT__##[0]'
     * @example '`bar.baz[1]`[3]' => '`bar.baz[1]`##__SPLIT__##[3]'
     */
    const pathPartWithoutArrayBracketsOutsideQuotationMarks = pathPart.replace(
      /(\[\d+])(?=(?:[^`]*`[^`]*`)*[^`]*$)/g,
      (match) => {
        return `${splitCharacter}${match}`;
      },
    );

    return pathPartWithoutArrayBracketsOutsideQuotationMarks
      .replace(/`/g, "")
      .split(splitCharacter)
      .map((pathPartSegment) => {
        if (/^\[\d+\]$/.test(pathPartSegment)) {
          return parseInt(pathPartSegment.replace("[", "").replace("]", ""), 10);
        }
        return pathPartSegment;
      });
  });
};
